import { timeFormat, timeParse } from 'd3-time-format';


/*
Date/time functions are now split it two categories:
(1) 'format' functions - convert server date to different versions of client date
(2) 'get' functions - any date manipulations, (!!!) input and output is server date

* The idea is to do any date manipulations in server date format, and format to client date explicitly when needed
* Reminder - we use local date '2023-04-28 14:15:54.800' on the server, ISO dates will not work
* I assume most date manipulations can be done with combination of functions below

Use notes:
* If you need to display custom format, use "formatDateDef" with formatStr


-- Functions ----------------
Format dates to display: (server date or "now" -> client date)
- formatDateDef - default, accepts formatStr
- formatDatetimeDef - default date+time
- formatDatetimeRel
- formatDateYMD
- formatDatetimeYMD

Date manipulations: (server date or "now" -> server date or number)
- getDateCreate - main func to get server date
- getDateAdd
- getDateDiff


-- ToDo ---------------------
- Remove parseDateValue(), getTime(), getAgeYrs(), esriDate()
- Should all "format" functions accept "now", similar to formatDatetimeDef?
- Are formatDateDef / formatDateYMD ok names? Need names such as one is not included in another, for easier search
- Need to test this all in Safari and Edge
*/



let systemDateFormat = '%m/%d/%Y';
let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let serverTimeZone = 'America/Los_Angeles';

export function setTimeZone(v) {
  timeZone = v;
}

export function setServerTimeZone(v) {
  serverTimeZone = v;
}

export function setDateFormat(f) {
  const options = { 'DD/MM/YYYY': '%d/%m/%Y', 'MM/DD/YYYY': '%m/%d/%Y' };
  systemDateFormat = options[f] || '%m/%d/%Y';
}
export function getDateFormat() {
  return systemDateFormat;
}

const timeFormatCache = {};

export function getServerTimezoneOffsetMinutes(timeZoneName = serverTimeZone) {
  const d = new Date();
  const utcDate = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(d.toLocaleString('en-US', { timeZone: timeZoneName }));
  const offset = (tzDate.getTime() - utcDate.getTime()) / 6e4;
  return -1 * offset;
}

export function getDate(dateYMD) {
  // is it still needed?
  // special logic for Safari
  if (!dateYMD || dateYMD?.length < 10) return ''; // 10 for short date 2000-01-01
  if (dateYMD?.length === 10) dateYMD += ' 00:00:00';
  if (dateYMD[4] !== '-') return new Date(dateYMD);

  let dateSafari = dateYMD.replace(' ', 'T');
  const date = new Date(dateSafari);
  return date;
}

// main format function, allows custom format
export function formatDateDef(value, formatStr = systemDateFormat) {
  // formatStr should match d3-time-format https://github.com/d3-time-format

  if (!value) return '';
  if (typeof (value) !== 'string') return value.toString();

  if (formatStr) {
    let f = timeFormatCache[formatStr];
    if (f == null) {
      f = timeFormat(formatStr);
      timeFormatCache[formatStr] = f;
    }
    value = getDate(value);
    return f(value);
  }
  return value;
}

// formatDate shortcuts
export function formatDateYMD(dateYMD) {
  if (!dateYMD) return '';
  return formatDateDef(dateYMD, '%Y-%m-%d');
}
export function formatDatetimeYMD(dateYMD) {
  if (!dateYMD) return '';
  return formatDateDef(dateYMD, '%Y-%m-%d %I:%M %p');
}
export function formatDatetimeDef(datetimeYMD) {
  if (!datetimeYMD) return '';
  if (datetimeYMD === 'now') datetimeYMD = getDateCreate('now');

  const f = getDateFormat() + ' %I:%M %p';
  const dateFormatted = formatDateDef(datetimeYMD, f);

  // debug dates for RetireVillage
  if (dateFormatted?.includes('NaN') && root.app.statsAddErrorLimited) {
    root.app.statsAddErrorLimited({
      errorName: 'formatDatetime',
      errorLimit: 2,
      errorInfo: { message: 'format datetime', dateFinalFormatted: dateFormatted, dateIntermediate: datetimeYMD, dateOriginal: datetimeYMD, f },
    });
  }
  return dateFormatted;
}

// special formatting
export function formatDatetimeRel(datetime, addZero) {
  // only works with server dates
  if (!datetime || typeof datetime !== 'string') return '';

  let value = datetime;

  const d = getDate(value);
  const now = new Date();
  if (!d || !d.getDate()) return value;

  // const diffMS = now - d;
  const diffMS = getTimeDiff(null, datetime, 'ms');
  const diffS = Math.round(diffMS / 1000);
  const diffMins = Math.round(diffMS / 60000, 0);
  let dateFormatted = '';
  let info = '';

  // future, same year
  if (diffMS < 0 && d.getFullYear() == now.getFullYear()) {
    dateFormatted = formatDateDef(datetime, '%b %d');
    info = 'future, same year';
  }
  // future, another year
  else if (diffMS < 0) {
    dateFormatted = formatDateDef(value);
    info = 'future, another year';
  }
  // -- past --
  // less than a minute ago
  else if (diffMS < 60000) {
    dateFormatted = (diffS) + ' sec. ago';
    info = 'less than a minute ago';
  }
  // less than an hour ago
  else if (diffMins < 60) {
    dateFormatted = diffMins + ' min. ago';
    info = 'less than an hour ago';
  }
  // same day, but more than an hour ago
  else if (d.getDate() == now.getDate() && d.getMonth() == now.getMonth() && d.getFullYear() == now.getFullYear()) {
    dateFormatted = formatDateDef(value, '%I:%M %p');
    info = 'same day, but more than an hour ago';
  }
  // same year
  else if (d.getFullYear() == now.getFullYear()) {
    dateFormatted = formatDateDef(datetime, '%b %d');
    info = 'same year';
  }
  // more than a year ago
  else {
    const f = getDateFormat();
    dateFormatted = formatDateDef(value, f);
    info = 'more than a year ago';
  }

  // console.log('date:', dateFormatted);
  // debug dates
  if (dateFormatted?.includes('NaN') && root.app.statsAddErrorLimited) {
    root.app.statsAddErrorLimited({
      errorName: 'formatDatetimeRel_',
      errorLimit: 2,
      errorInfo: { message: 'format relative', dateFinalFormatted: dateFormatted, dateOriginal: datetime, getDate: d, now, diffMS, diffS, diffMins },
    });
  }
  // console.log('info:', info);
  return dateFormatted;
}


// -----------------------------------------------------
// date manipulations - create server date, add days etc
export function getDateCreate(dateISO) {
  if (!dateISO) return '';
  const d = dateISO === 'now' ? new Date() : new Date(dateISO);

  if (dateISO.match(/\d{3}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}.\d{1}/)?.index === 1) {
    // already server format
    return dateISO.substring(0, 23);
  }
  if (dateISO.length === 10 && dateISO?.split('-')?.length === 3) {
    // if YYYY-MM-DD passed
    return dateISO + ' 00:00:00.000';
  }

  const serverTimeZoneOffset = getServerTimezoneOffsetMinutes(serverTimeZone);
  // console.log('d1:', serverTimeZoneOffset / 60, clientTimeZoneOffset / 60, serverTimeZone, d.toISOString());
  d.setMinutes(d.getMinutes() - serverTimeZoneOffset);
  let newValue = d.toISOString().replace('T', ' ').replace('Z', '');
  return newValue;
}

function diffInCalendarDays(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  const ua = Date.UTC(da.getUTCFullYear(), da.getUTCMonth(), da.getUTCDate());
  const ub = Date.UTC(db.getUTCFullYear(), db.getUTCMonth(), db.getUTCDate());
  return Math.round((ua - ub) / 86400000);// 86_400_000 ms
}

export function getTimeDiff(t1, t2, units = 'ms') {
  if (t1 === null) t1 = getDateCreate('now');
  if (t2 === null) t2 = getDateCreate('now');
  if (!(getDate(t1)) || !(getDate(t2))) return null;

  const time1 = getDate(t1).getTime();
  const time2 = getDate(t2).getTime();
  let timeDiffMS = time1 - time2;
  if (units === 'days') return diffInCalendarDays(t1, t2);
  if (units === 'ms') return timeDiffMS;
  if (units === 'seconds') return Math.round((timeDiffMS / 1000) * 10) / 10;
  if (units === 'minutes') return Math.round((timeDiffMS / 1000 / 60) * 10) / 10;
  if (units === 'hours') return Math.round((timeDiffMS / 1000 / 3600) * 10) / 10;
  if (units === 'days') return Math.round((timeDiffMS / 1000 / 3600 / 24) * 10) / 10;
  return null;
}

export function getDateAdd(dateServer, n, units) {
  if (!dateServer) dateServer = getDateCreate('now');
  n = Number(n) || 0;

  let d1 = getDate(dateServer);
  d1.setMinutes(d1.getMinutes() - d1.getTimezoneOffset());
  // console.log('add:', dateUTC, d1.toISOString());
  if (units === 'minutes') d1.setMinutes(d1.getMinutes() + n);
  if (units === 'hours') d1.setHours(d1.getHours() + n);
  if (units === 'days') d1.setDate(d1.getDate() + n);
  if (units === 'months') d1.setMonth(d1.getMonth() + n);
  if (units === 'years') d1.setFullYear(d1.getFullYear() + n);
  // console.log('add2:', d1.toISOString());
  let newValue = d1.toISOString().replace('T', ' ').replace('Z', '');
  return newValue;
}

/*
    const errorExample = {
      'message': 'format datetime',
      'dateOriginal': '2023-04-28 14:15:54.800',
      'dateIntermediate': '28/04/2023, 02:15:54 pm',
      'dateFinalFormatted': 'NaN/NaN/0NaN 12:NaN AM',
      'f': '%d/%m/%Y %I:%M %p',
      'dateFormatted2': 'NaN/NaN/0NaN 12:NaN AM',
    };
    const normal = {
      'f': '%m/%d/%Y %I:%M %p',
      'dateOriginal': '2023-04-28 14:15:54.800',
      'dateIntermediate': '04/28/2023, 02:15:54 PM',
      'dateFinalFormatted': '04/28/2023 02:15 PM',
    };
*/


// to remove ----------------------
export function parseDateValue(value, formatStr = '%m/%d/%Y') {
  // console.log('v:', value, value?.length);
  if (!value || value?.length < 8) return null;

  if ((value || '').length == 10) {
    let v = timeParse(formatStr);
    const newValue = v(value);
    // console.log('new:', formatStr, value, newValue, newValue === 'Invalid Date');
    // it does not catch invalid 00/07/2023
    if (newValue && ['%d/%m/%Y', '%m/%d/%Y'].includes(formatStr)) {
      if (value.split('/').includes('00')) return null;
    }
    return newValue;
  }
  return null;
}

export function getTime(isoDate) {
  if (!isoDate) return '';
  return isoDate.substring(11, 19);
}

export function getAgeYrs(date) {
  if (!date || !(getDate(date))) return date;
  return (((new Date()).getTime() - (getDate(date)).getTime()) / 365 / 24 / 3600000).toFixed(0);
}


export function isDST() {
  const date = new Date();
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  const stdTime = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  return date.getTimezoneOffset() < stdTime;
}
